import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Tab,
  Tabs,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type {
  EmailTemplate,
  EmailTemplateResponse,
} from "../api/clientManagement";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";

interface EmailTemplatesTabProps {
  isCentralAdmin: boolean;
  selectedTemplateKey: string;
  onTemplateChange: (templateKey: string) => void;
  templateOptions: { value: string; label: string }[];
  templateQuery: UseQueryResult<EmailTemplateResponse>;
  updateTemplateMutation: UseMutationResult<
    EmailTemplateResponse,
    unknown,
    {
      name?: string;
      subject: string;
      html_body: string;
      text_body: string;
      is_active?: boolean;
    }
  >;
}

export const EmailTemplatesTab: React.FC<EmailTemplatesTabProps> = ({
  isCentralAdmin,
  selectedTemplateKey,
  onTemplateChange,
  templateOptions,
  templateQuery,
  updateTemplateMutation,
}) => {
  const template = templateQuery.data?.data;
  const [editorTab, setEditorTab] = useState<"edit" | "preview">("edit");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [textBody, setTextBody] = useState("");
  const [isActive, setIsActive] = useState(true);

  const previewValues = {
    firstName: "Alex",
    email: "alex@example.com",
  };

  const applyPreviewValues = (value: string) =>
    value
      .replace(/{{\s*firstName\s*}}/gi, previewValues.firstName)
      .replace(/{{\s*email\s*}}/gi, previewValues.email);

  useEffect(() => {
    if (!template) return;
    setName(template.name || "");
    setSubject(template.subject || "");
    setHtmlBody(template.html_body || "");
    setTextBody(template.text_body || "");
    setIsActive(Boolean(template.is_active));
  }, [template]);

  useEffect(() => {
    updateTemplateMutation.reset();
  }, [selectedTemplateKey]);

  if (!isCentralAdmin) {
    return (
      <SettingsSection
        title="Email Templates"
        description="Central admin access required."
      >
        <Alert severity="warning">
          You do not have permission to manage email templates.
        </Alert>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title="Email Templates"
      description="Manage system email wording."
    >
      <SectionCard title="Email Content">
        {templateQuery.isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2">Loading template...</Typography>
          </Stack>
        ) : templateQuery.isError ? (
          <Alert severity="error">Failed to load email template.</Alert>
        ) : (
          <Stack spacing={2}>
            <TextField
              select
              label="Template"
              value={selectedTemplateKey}
              size="small"
              onChange={(event) => onTemplateChange(event.target.value)}
            >
              {templateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Tabs
              value={editorTab}
              onChange={(_event, value) => setEditorTab(value)}
              textColor="primary"
              indicatorColor="primary"
              sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Tab value="edit" label="Edit" sx={{ textTransform: "none" }} />
              <Tab
                value="preview"
                label="Preview"
                sx={{ textTransform: "none" }}
              />
            </Tabs>

            {editorTab === "edit" && (
              <Stack spacing={2}>
                <TextField
                  label="Template Name"
                  value={name}
                  size="small"
                  onChange={(event) => setName(event.target.value)}
                />
                <TextField
                  label="Subject"
                  value={subject}
                  size="small"
                  onChange={(event) => setSubject(event.target.value)}
                />
                <TextField
                  label="Text Body"
                  multiline
                  minRows={8}
                  value={textBody}
                  onChange={(event) => setTextBody(event.target.value)}
                />
                <TextField
                  label="HTML Body"
                  multiline
                  minRows={10}
                  value={htmlBody}
                  onChange={(event) => setHtmlBody(event.target.value)}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(_event, checked) => setIsActive(checked)}
                    />
                  }
                  label="Template Active"
                />
              </Stack>
            )}

            {editorTab === "preview" && (
              <Stack spacing={2}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Placeholder preview values: {previewValues.firstName},{" "}
                  {previewValues.email}
                </Alert>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {applyPreviewValues(subject) || "(No subject)"}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Text Preview
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", mt: 1 }}
                  >
                    {applyPreviewValues(textBody) || "(No text body)"}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    HTML Preview
                  </Typography>
                  <Box
                    sx={{ mt: 1 }}
                    dangerouslySetInnerHTML={{
                      __html: applyPreviewValues(htmlBody) || "<p>(No HTML body)</p>",
                    }}
                  />
                </Paper>
              </Stack>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary">
                Supported placeholders: {"{{firstName}}"}, {"{{email}}"}
              </Typography>
            </Box>
            <Box>
              <Button
                variant="contained"
                disabled={
                  updateTemplateMutation.isPending ||
                  !subject.trim() ||
                  !textBody.trim() ||
                  !htmlBody.trim()
                }
                onClick={() =>
                  updateTemplateMutation.mutate({
                    name: name.trim(),
                    subject: subject.trim(),
                    html_body: htmlBody.trim(),
                    text_body: textBody.trim(),
                    is_active: isActive,
                  })
                }
              >
                Save Template
              </Button>
            </Box>
            {updateTemplateMutation.isError && (
              <Alert severity="error">Failed to save template.</Alert>
            )}
            {updateTemplateMutation.isSuccess && (
              <Alert severity="success">Template saved successfully.</Alert>
            )}
          </Stack>
        )}
      </SectionCard>
    </SettingsSection>
  );
};
